import { useEffect } from "react";

const LOADER_SCRIPT_ID = "hs-script-loader";
const CHAT_IFRAME_CONTAINER = "#hubspot-messages-iframe-container";

/**
 * Loads the HubSpot tracking script and controls the HubSpot Conversations (live chat) widget.
 *
 * The loader script (js.hs-scripts.com) also provides HubSpot analytics, the cookie banner and the
 * ads pixel, so it is always loaded. The chat widget itself is only shown when `chatEnabled`
 * (env switch) and `showChat` (runtime condition) are both true. `loadImmediately: false` is set
 * before the loader runs so HubSpot never auto-renders the chat bubble on its own.
 *
 * @param {string} portalId HubSpot portal id
 * @param {{ chatEnabled?: boolean, showChat?: boolean }} options
 */
export const useHubspotChat = (portalId, { chatEnabled = false, showChat = true } = {}) => {
  // Tracking loader: once per portal, never removed.
  useEffect(() => {
    if (typeof window === "undefined" || !portalId) return;

    window.hsConversationsSettings = {
      ...(window.hsConversationsSettings || {}),
      loadImmediately: false,
    };

    if (document.getElementById(LOADER_SCRIPT_ID)) return;

    const script = document.createElement("script");
    script.id = LOADER_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.defer = true;
    script.src = `https://js.hs-scripts.com/${portalId}.js`;
    script.onerror = () => console.warn("HubSpot script failed to load");
    document.body.appendChild(script);
  }, [portalId]);

  // Chat widget on/off.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const wantChat = Boolean(chatEnabled && showChat);
    let cancelled = false;

    const apply = () => {
      if (cancelled) return;
      const widget = window.HubSpotConversations && window.HubSpotConversations.widget;
      if (!widget) return;
      try {
        if (wantChat) {
          widget.load();
        } else {
          widget.remove();
        }
      } catch (error) {
        console.warn("HubSpot chat widget toggle failed", error);
      }
      if (!wantChat) {
        document.querySelectorAll(CHAT_IFRAME_CONTAINER).forEach((el) => {
          try { el.remove(); } catch {}
        });
      }
    };

    if (window.HubSpotConversations) {
      apply();
    } else {
      window.hsConversationsOnReady = [...(window.hsConversationsOnReady || []), apply];
    }

    return () => {
      cancelled = true;
    };
  }, [chatEnabled, showChat]);

  return { chatEnabled: Boolean(chatEnabled) };
};

export default useHubspotChat;
